import React, {useState, useEffect} from "react";
import {Box, Button, TextField, Typography, Paper, Alert, CircularProgress, Link, InputAdornment, IconButton} from "@mui/material";
import {Visibility, VisibilityOff} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {Link as RouterLink, useSearchParams, useNavigate} from "react-router-dom";
import {verifyResetToken, resetPassword} from "../../api/auth";
import LanguageSelector from "../../components/common/LanguageSelector";
import {useForm} from "../../utils/useForm";
import {FORM_VALIDATION_CONFIGS} from "../../utils/formValidation";
import FormField from "../../components/common/FormField";

const ResetPasswordPage: React.FC = () => {
  const {t} = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [email, setEmail] = useState<string>("");

  const form = useForm({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationConfig: {
      ...FORM_VALIDATION_CONFIGS.RESET_PASSWORD,
      confirmPassword: {
        ...FORM_VALIDATION_CONFIGS.RESET_PASSWORD.confirmPassword,
        custom: (value: string) => {
          if (value !== form.values.newPassword) {
            return "Passwords do not match";
          }
          return null;
        },
      },
    },
    onSubmit: async (values) => {
      if (!token) {
        setError("Invalid reset link");
        return;
      }

      try {
        const response = await resetPassword(token, values.newPassword);
        setSuccess(response.message);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } catch (err: any) {
        console.error("Password reset error:", err);
        setError(err.response?.data?.message || "Failed to reset password. Please try again.");
        throw err;
      }
    },
    onError: (errors) => {
      console.log("Validation errors:", errors);
    },
  });

  useEffect(() => {
    if (!token) {
      setError("Invalid reset link. Please request a new password reset.");
      setVerifying(false);
      return;
    }

    const verifyToken = async () => {
      try {
        const response = await verifyResetToken(token);
        setEmail(response.email);
        setVerifying(false);
      } catch (err: any) {
        console.error("Token verification error:", err);
        setError(err.response?.data?.message || "Invalid or expired reset link. Please request a new password reset.");
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  if (verifying) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#f5f6fa"
        sx={{
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        }}>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: "100%",
            maxWidth: 400,
            borderRadius: 2,
            boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.15)",
          }}>
          <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column">
            <CircularProgress size={40} sx={{mb: 2}} />
            <Typography variant="h6">Verifying reset link...</Typography>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="#f5f6fa"
      sx={{
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
      }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: "100%",
          maxWidth: 400,
          borderRadius: 2,
          boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.15)",
        }}>
        {/* Language Selector */}
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <LanguageSelector />
        </Box>

        <Box textAlign="center" mb={3}>
          <Typography variant="h5" component="h1" fontWeight="bold" color="primary">
            SmartFarm
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Reset Password
          </Typography>
        </Box>

        {email && (
          <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
            Reset password for: <strong>{email}</strong>
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{mb: 2}}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{mb: 2}}>
            {success}
            <br />
            Redirecting to login page...
          </Alert>
        )}

        {!success && (
          <form onSubmit={form.handleSubmit}>
            <TextField
              label="New Password"
              name="newPassword"
              type={showPassword ? "text" : "password"}
              value={form.values.newPassword}
              onChange={form.handleChange("newPassword")}
              fullWidth
              margin="normal"
              variant="outlined"
              required
              autoComplete="new-password"
              disabled={form.isSubmitting}
              error={!!form.errors.newPassword}
              helperText={form.errors.newPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Confirm New Password"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={form.values.confirmPassword}
              onChange={form.handleChange("confirmPassword")}
              fullWidth
              margin="normal"
              variant="outlined"
              required
              autoComplete="new-password"
              disabled={form.isSubmitting}
              error={!!form.errors.confirmPassword}
              helperText={form.errors.confirmPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                borderRadius: 1,
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
              }}
              type="submit"
              disabled={form.isSubmitting}>
              {form.isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Reset Password"}
            </Button>

            <Box textAlign="center" mt={2}>
              <Typography variant="body2" color="text.secondary">
                Remember your password?{" "}
                <Link component={RouterLink} to="/login" color="primary">
                  Sign in
                </Link>
              </Typography>
            </Box>
          </form>
        )}
      </Paper>
    </Box>
  );
};

export default ResetPasswordPage;
